import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    getAdminViewPermissions,
    type IPermissionActionDescriptor,
    type IUserAuthPDPModel,
    ResourceGroup,
    ResourceType,
} from '@experiences/interfaces';
import type { PropsWithChildren } from 'react';
import React, {
    createContext,
    useEffect,
    useMemo,
} from 'react';
import {
    useDispatch,
    useSelector,
} from 'react-redux';
import useSWR from 'swr';

import useUserInfo from '../../auth/hooks/UserInfo';
import { fetchUserAuthPDP } from '../../services/centralizedaccess/CentralizedAccessService';
import {
    updateAdminStatus,
    updateCanViewAdmin,
} from '../../store/action/UserProfileAction';
import {
    accountGlobalId,
    isOrgAdminSelector,
    isTenantAdminSelector,
} from '../../store/selectors';

const UserAuthPDPModelContext = createContext<IUserAuthPDPModel | undefined>(undefined);

// TODO: DO NOT DELETE. This will be used in the future to get user permissions across admin
// const useGetUserPermissions = ({
//     resource, resourceType, action, tenantId = '',
// }: { resource: string; resourceType: string; action: AuthAction; tenantId?: string }) => {
//     const permissions = React.useContext(UserAuthPDPModelContext);

//     const filteredPermission = permissions?.actions?.filter((actionIterator) =>
//         actionIterator.ResourceGroup.toLowerCase() === resource.toLowerCase()
//         && actionIterator.ResourceType.toLowerCase() === resourceType.toLowerCase()
//         && actionIterator.ResourceAction.toUpperCase() === action.toUpperCase()
//         && actionIterator.Scope.toLowerCase().includes(tenantId.toLowerCase()));

//     if (!filteredPermission) {
//         return false;
//     }

//     return filteredPermission.length > 0;
// };

export const useHasAnyPermission = (
    requiredActions: IPermissionActionDescriptor[],
    tenantId = '',
): boolean => {
    const isOrgAdmin = useSelector(isOrgAdminSelector);
    const permissions = React.useContext(UserAuthPDPModelContext);

    if (isOrgAdmin) {
        return true;
    }

    if (!permissions?.actions || requiredActions.length === 0) {
        return false;
    }

    return requiredActions.some(({
        resource, resourceType, action,
    }) =>
        permissions.actions.some((perm) =>
            perm.ResourceGroup.toLowerCase() === resource.toLowerCase()
            && perm.ResourceType.toLowerCase() === resourceType.toLowerCase()
            && perm.ResourceAction.toUpperCase() === action.toUpperCase()
            && perm.Scope.toLowerCase().includes(tenantId.toLowerCase())
        )
    );
};

export const UserAuthPDPModelProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const EnableTenantAdminControl = useFeatureFlagValue(Features.EnableTenantAdminControl.name);

    const { token } = useUserInfo();

    const isOrgAdmin = useSelector(isOrgAdminSelector);
    const isTenantAdmin = useSelector(isTenantAdminSelector);
    const userId = useSelector(accountGlobalId);

    const dispatch = useDispatch();

    const {
        data: userAuthPDPModel, isLoading, error,
    } = useSWR(
        EnableTenantAdminControl && token && userId ? {
            url: `/api/centralizedaccess/getallactions`,
            payload: {
                RequestParams: {
                    Scope: '/',
                    SecurableId: userId,
                },
            },
        } : null,
        fetchUserAuthPDP
    );

    useEffect(() => {
        // If user is not an org admin or tenant admin, and userAuthPDPModel is not null, then dispatch updateAdminStatus
        if (!isOrgAdmin && !isTenantAdmin && userAuthPDPModel?.actions?.some(action =>
            action.ResourceGroup.toLowerCase() === ResourceGroup.CentralizedAccess
            && action.ResourceType.toLowerCase() === ResourceType.AdministrationPage
            && action.ResourceAction.toLowerCase() === 'view')) {
            dispatch(updateAdminStatus('TENANT_ADMIN'));
        // if data is no longer loading and userAuthPDPModel is still null, then dispatch updateAdminStatus
        } else if (!isLoading && !userAuthPDPModel && error) {
            dispatch(updateAdminStatus());
        }
    }, [ dispatch, error, isLoading, isOrgAdmin, isTenantAdmin, userAuthPDPModel ]);

    const adminViewPermissions = useMemo(() => getAdminViewPermissions(), []);

    const canViewAdmin = useMemo(() => {
        if (!userAuthPDPModel?.actions) {
            return false;
        }
        return adminViewPermissions.some(({
            resource, resourceType, action,
        }) =>
            userAuthPDPModel.actions.some((perm) =>
                perm.ResourceGroup.toLowerCase() === resource.toLowerCase()
                && perm.ResourceType.toLowerCase() === resourceType.toLowerCase()
                && perm.ResourceAction.toUpperCase() === action.toUpperCase()
            )
        );
    }, [ adminViewPermissions, userAuthPDPModel ]);

    useEffect(() => {
        if (canViewAdmin) {
            dispatch(updateCanViewAdmin(true));
        }
    }, [ canViewAdmin, dispatch ]);

    // If error, short circuit and return children
    return <UserAuthPDPModelContext.Provider value={userAuthPDPModel}>
        {isOrgAdmin || isTenantAdmin || error
            ? children
            : (
                isLoading && isTenantAdmin == null
                    ? undefined
                    : children
            )}
    </UserAuthPDPModelContext.Provider>;
};
